import PropTypes from 'prop-types';
import styled from 'styled-components';
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import { getDocumentWidth, applyVerticalStyles, shapedPropsWarning, pageContext } from 'ctm-util-functions';

import { withStyleProxy } from '../../higher_order_components/with_style_proxy';
import styles from './tooltip.css';

class _Tooltip extends PureComponent {

    constructor(props) {
        super(props);
        // @todo remove once all shapedProps are gone
        shapedPropsWarning(props, 'Tooltip');
        this.styles = applyVerticalStyles(styles, 'tooltip');
        const { arrowStyles, bubbleStyles } = this.getPlacementStyles(props);
        this.state = {
            arrowStyles,
            bubbleStyles
        };
    }

    /**
     * Called when the component is mounted.
     */
    componentDidMount = () => {
        window.root?.addEventListener('click', this.handleDocumentEvent);
        window.addEventListener('scroll', this.handleDocumentEvent);
        window.addEventListener('resize', this.resizeEvent);
        document.querySelectorAll("div[class^='modal__modal_body']").forEach((modal) => {
            modal.addEventListener('scroll', this.handleDocumentEvent);
        });
    };

    /**
     * Invoked immediately before a component is unmounted and destroyed
     */
    componentWillUnmount = () => {
        window.root?.removeEventListener('click', this.handleDocumentEvent);
        window.removeEventListener('scroll', this.handleDocumentEvent);
        window.removeEventListener('resize', this.resizeEvent);
        document.querySelectorAll("div[class^='modal__modal_body']").forEach((modal) => {
            modal.removeEventListener('scroll', this.handleDocumentEvent);
        });
    };

    /**
     * Handler for document Events.
     * Use contains method on a node to check what was clicked.
     * @param  {Object} event - the event.
     */
    handleDocumentEvent = (event) => {
        const { onClickOutside } = this.props;
        if (!this.node.contains(event.target) && onClickOutside) {
            onClickOutside(event);
        }
    };

    resizeEvent = (event) => {
        const { onClickOutside } = this.props;
        if (onClickOutside) {
            onClickOutside(event);
        } else {
            const { bubbleStyles, arrowStyles } = this.getPlacementStyles(this.props);
            this.setState({ bubbleStyles, arrowStyles });
        }
    };

    getTriggerElement = (trigger) => {
        const triggerRect = trigger.getBoundingClientRect();
        if (document.getElementById('overlay')) {
            return this.calculateModalTooltipValues(triggerRect);
        }
        return triggerRect;
    };

    /**
     * Get the Placement Styles for the ToolTip.
     * @param  {String} placement - placement property.
     * @param  {Object} triggerRect - size of the trigger element and its position relative to the viewport.
     * @returns {Object} - Styles for the ToolTip.
     */
    getPlacementStyles = ({ placement, trigger, offset }) => {
        const { customGetPlacementStyles } = this.props;

        if (customGetPlacementStyles) {
            return customGetPlacementStyles(this.props);
        }

        const bubbleStyles = {};
        const arrowStyles = {};
        const { left, right, top, bottom, width } = this.getTriggerElement(trigger);
        const maxWidth = this.styles.max_width || '0';
        const tooltipContainerWidth = (maxWidth.replace(/[^0-9.]/g, '') * 10);
        const halfTooltipWidth = tooltipContainerWidth / 2;
        const documentWidth = getDocumentWidth();
        const minLeftValue = tooltipContainerWidth / 2;
        const maxLeftValue = documentWidth - minLeftValue;
        switch (placement) {
            case 'top':
                bubbleStyles.left = left + ((right - left) / 2);
                bubbleStyles.top = top;
                // If the left value it was going to use is going to be off the screen, we need to give it a min left
                // so that it won't go off the screen
                if (bubbleStyles.left < minLeftValue) {
                    // In this case, we need to subtract the difference of the left value
                    // from half the width of a tooltip in order to shift the arrow to the left more.
                    arrowStyles.left = bubbleStyles.left;
                    bubbleStyles.left = minLeftValue;
                } else if (bubbleStyles.left > maxLeftValue) {
                    // In this case, we need to add the difference between the calculated and max left value
                    // to half the width of the tooltip to shift the arrow to the right more.
                    arrowStyles.left = halfTooltipWidth + bubbleStyles.left - maxLeftValue;
                    bubbleStyles.left = maxLeftValue;
                }
                break;
            case 'bottom':
                bubbleStyles.left = left + ((right - left) / 2);
                bubbleStyles.top = bottom;
                break;
            case 'right':
                bubbleStyles.left = right;
                bubbleStyles.top = top + ((bottom - top) / 2);
                break;
            case 'left':
                bubbleStyles.left = left;
                bubbleStyles.top = top + ((bottom - top) / 2);
                break;
            case 'bottom_left':
                bubbleStyles.left = left + offset;
                bubbleStyles.top = bottom;
                break;
            case 'bottom_right':
                bubbleStyles.left = right - (width + offset);
                bubbleStyles.top = bottom;
                break;
            default:
                bubbleStyles.left = right;
                bubbleStyles.top = top + ((bottom - top) / 2);
        }

        return { bubbleStyles, arrowStyles };
    };

    /**
     * Calculate tooltip position inside a modal
     * @param {Object} rect - positions of the rectangular tooltip
     * @returns {Object} - returns new modal tooltip positions object
     */
    calculateModalTooltipValues = (rect) => {
        return {
            left: rect.left,
            right: rect.right,
            top: rect.top,
            bottom: rect.bottom,
            width: rect.width
        };
    };

    getToolTipRef = (node) => {
        this.node = node;
    };

    /**
     * Render the component based on the variant, placement and trigger properties.
     * @returns {Node} - Returns the Tooltip React Component.
     */
    render = () => {
        // eslint-disable-next-line react/prop-types
        const { placement, variant, trigger, children, specialFlags, dataId } = this.props;
        const NewToolTipContainer = styled.div`
          max-height: 59rem;
          width: 23.75rem;
          background: #ffffff;
`;
        const { bubbleStyles, arrowStyles } = this.state;

        return (
            <div
                ref={this.getToolTipRef}
                className={classNames(this.styles.root, this.styles[variant], trigger && this.styles[`placement_${placement}`])}
                style={bubbleStyles}
            >
                <div
                    className={classNames(
                        this.styles.tooltip_arrow,
                        this.styles[`placement_${placement}__tooltip_arrow`],
                        this.styles[`placement_${placement}__${variant}__tooltip_arrow`]
                    )}
                    style={arrowStyles}
                />
                {
                    specialFlags !== 'results_hnc_tooltip'
                        ? <div className={classNames(this.styles.tooltip_inner, this.styles[`${variant}__tooltip_inner`])} data-id={dataId}>{children}</div>
                        : <NewToolTipContainer className={classNames(this.styles.tooltip_inner, this.styles[`${variant}__tooltip_inner`])}>{children}</NewToolTipContainer>
                }
            </div>
        );
    };

}

_Tooltip.propTypes = {
    /**
     * Placement for the Tooltip.
     */
    placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left', 'bottom_left', 'bottom_right']),
    /**
     * Variant of the Tooltip.
     */
    variant: PropTypes.string,
    /**
     * trigger element of the Tooltip.
     */
    trigger: PropTypes.object,
    /**
     * Trigger components padding
     */
    offset: PropTypes.number,
    /**
     * Property used as onClickOutside handler.
     */
    onClickOutside: PropTypes.func,

    children: PropTypes.node,

    customGetPlacementStyles: PropTypes.func
};

_Tooltip.defaultProps = {
    placement: 'right',
    variant: 'brand_base_0',
    offset: 0
};

// Apply the visual Props Proxy Higher Order Component and export the Enhanced component.
export const Tooltip = withStyleProxy(_Tooltip);

// Export the non decorated component in order to test it by itself.
export { _Tooltip };
